import { Box, Group, Stack, Text, Paper, ActionIcon, Tooltip } from '@mantine/core';
import { IconDownload, IconX, IconFile } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { ExistingFileInfo } from './types/ObjectFormTypes';
import { downloadFile } from '../../services/MapComponentsService';
import { useTranslation } from "../../hooks/useTranslation";
import useTransientStore from "../../data/transcientStore";

interface ViewModeFileListProps {
    files: ExistingFileInfo[];
    onFileRemove: (fileId: number) => void;
    label?: string;
    description?: string;
    disabled?: boolean;
}

export default function ViewModeFileList({
    files,
    onFileRemove,
    label,
    description,
    disabled = false
}: ViewModeFileListProps) {
    const selectedAddressSystem = useTransientStore((state) => state.selectedAddressSystem);
    const addrIdFromStore = selectedAddressSystem?.id || 0;

    const { getTranslation } = useTranslation({ pageId: 338, addressSystemId: addrIdFromStore || 0, autoLoad: true });

    const resolvedLabel = label ?? getTranslation("LABEL_FILES") ?? "Files";

    const handleDownload = async (file: ExistingFileInfo) => {
        try {
            await downloadFile(file.id, file.name);
            notifications.show({
                title: getTranslation("TITLE_SUCCESS") || 'Success',
                message:
                    getTranslation("MESSAGE_DOWNLOADING_FILE") ||
                    `Downloading ${file.name}`,
                color: 'green',
                autoClose: 3000,
                position: 'bottom-left',
            });
        } catch (error: any) {
            notifications.show({
                title: getTranslation("TITLE_DOWNLOAD_FAILED") || 'Download Failed',
                message:
                    error.message ||
                    getTranslation("MESSAGE_DOWNLOAD_FAILED") ||
                    'Failed to download file',
                color: 'red',
                autoClose: 5000,
                position: 'bottom-left',
            });
        }
    };

    const handleRemove = (fileId: number, fileName: string) => {
        onFileRemove(fileId);
        notifications.show({
            title: getTranslation("TITLE_FILE_REMOVED") || 'File Removed',
            message:
                getTranslation("MESSAGE_FILE_REMOVED_ON_SAVE") ||
                `${fileName} will be deleted when you save`,
            color: 'blue',
            autoClose: 3000,
            position: 'bottom-left',
        });
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return getTranslation("LABEL_ZERO_BYTES") || '0 Bytes';
        const k = 1024;
        const sizes = [
            getTranslation("LABEL_BYTES") || 'Bytes',
            getTranslation("LABEL_KB") || 'KB',
            getTranslation("LABEL_MB") || 'MB',
            getTranslation("LABEL_GB") || 'GB'
        ];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
    };

    if (files.length === 0) {
        return (
            <Stack gap="md">
                <Box>
                    <Text size="sm" fw={500}>{resolvedLabel}</Text>
                    {description && (
                        <Text size="xs" c="dimmed">{description}</Text>
                    )}
                </Box>
                <Text size="sm" c="dimmed" ta="center" py="lg">
                    {getTranslation("LABEL_NO_FILES_UPLOADED") || "No files uploaded"}
                </Text>
            </Stack>
        );
    }

    return (
        <Stack gap="md">
            <Box>
                <Group justify="space-between" align="flex-end" mb={4}>
                    <Group gap="xs">
                        <Text size="sm" fw={500}>{resolvedLabel}</Text>
                        {files.length > 0 && (
                            <Text size="xs" c="dimmed" fw={500}>
                                ({files.length})
                            </Text>
                        )}
                    </Group>
                </Group>
                {description && (
                    <Text size="xs" c="dimmed">{description}</Text>
                )}
            </Box>

            <Box 
                style={{ 
                    maxHeight: files.length > 3 ? '400px' : 'none',
                    overflowY: files.length > 3 ? 'auto' : 'visible',
                    paddingRight: files.length > 3 ? '4px' : '0'
                }}
            >
                <Stack gap="xs">
                    {files.map((file) => (
                        <Paper key={file.id} p="md" withBorder radius="md">
                            <Group gap="sm" wrap="nowrap">
                                <IconFile size={18} style={{ flexShrink: 0 }} />
                                <Box style={{ flex: 1, minWidth: 0 }}>
                                    <Text size="sm" fw={500} truncate>
                                        {file.name}
                                    </Text>
                                    <Text size="xs" c="dimmed">
                                        {formatFileSize(file.size)}
                                    </Text>
                                    {file.fileHeader && (
                                        <Text size="xs" c="dimmed" mt={4}>
                                            {getTranslation("LABEL_HEADER") || "Header:"}{" "}
                                            {file.fileHeader}
                                        </Text>
                                    )}
                                </Box>
                                <Group gap="xs" style={{ flexShrink: 0 }}>
                                    <Tooltip label={getTranslation("LABEL_DOWNLOAD_FILE") || "Download file"}>
                                        <ActionIcon 
                                            color="blue" 
                                            variant="subtle"
                                            onClick={() => handleDownload(file)}
                                            size="sm"
                                        >
                                            <IconDownload size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                    <Tooltip label={getTranslation("LABEL_REMOVE_FILE") || "Remove file"}>
                                        <ActionIcon 
                                            color="red" 
                                            variant="subtle"
                                            onClick={() => handleRemove(file.id, file.name)}
                                            size="sm"
                                            disabled={disabled}
                                        >
                                            <IconX size={16} />
                                        </ActionIcon>
                                    </Tooltip>
                                </Group>
                            </Group>
                        </Paper>
                    ))}
                </Stack>
            </Box>
        </Stack>
    );
}
